import React, { memo, useEffect, useState } from 'react';
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import { CheckCircleOutline, CloudOffOutlined, ErrorOutline, Keyboard } from '@mui/icons-material';

/**
 * Format how long ago a timestamp was.
 * @param {number|null} ts - Epoch millis.
 * @param {number} now - Current epoch millis.
 * @returns {string} Relative time, e.g. "just now", "2 min ago".
 */
const formatAgo = (ts, now) => {
  if (!ts) return '';
  const seconds = Math.max(0, Math.round((now - ts) / 1000));
  if (seconds < 10) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  return new Date(ts).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

/**
 * Compute document statistics.
 * @param {import('@tiptap/core').Editor} editor - Editor.
 * @returns {{words: number, characters: number, fields: number}} Stats.
 */
const computeStats = (editor) => {
  const storage = editor.storage.characterCount;
  let fields = 0;
  editor.state.doc.descendants((node) => {
    if (node.type.name === 'templateField' || node.type.name === 'templateFieldBlock') fields += 1;
    return !node.isTextblock;
  });
  return {
    words: storage ? storage.words() : 0,
    characters: storage ? storage.characters() : 0,
    fields,
  };
};

/**
 * Save status label/icon.
 * @param {{status?: string, lastSavedAt?: number|null, error?: string|null}} saveState - Host save state.
 * @param {number} now - Current time.
 * @returns {{icon: React.ReactNode, label: string, color: string}|null} Display or null.
 */
const describeSave = (saveState, now) => {
  if (!saveState || !saveState.status) return null;
  switch (saveState.status) {
    case 'saving':
      return { icon: <CircularProgress size={11} thickness={6} />, label: 'Saving…', color: 'text.secondary' };
    case 'dirty':
      return { icon: null, label: 'Unsaved changes', color: 'text.secondary' };
    case 'error':
      return { icon: <ErrorOutline sx={{ fontSize: 14 }} />, label: saveState.error || 'Save failed, retrying', color: 'error.main' };
    case 'offline':
      return { icon: <CloudOffOutlined sx={{ fontSize: 14 }} />, label: 'Offline, will retry', color: 'warning.main' };
    case 'conflict':
      return { icon: <ErrorOutline sx={{ fontSize: 14 }} />, label: 'Changed elsewhere', color: 'error.main' };
    case 'saved':
      return {
        icon: <CheckCircleOutline sx={{ fontSize: 14 }} />,
        label: saveState.lastSavedAt ? `Saved ${formatAgo(saveState.lastSavedAt, now)}` : 'Saved',
        color: 'success.main',
      };
    default:
      return null;
  }
};

/**
 * Footer with words, characters, reading time, field count and save status.
 * Statistics refresh at most every 300ms after edits (never per keystroke render).
 * @param {{editor: import('@tiptap/core').Editor, saveState?: object, mode?: string, reviewCount?: number, onShowShortcuts?: () => void}} props - Props.
 * @returns {JSX.Element}
 */
const EditorStatusBar = ({ editor, saveState, mode = 'document', reviewCount = 0, onShowShortcuts }) => {
  const [stats, setStats] = useState(() => (editor ? computeStats(editor) : { words: 0, characters: 0, fields: 0 }));
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!editor) return undefined;
    let timer = null;
    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (!editor.isDestroyed) setStats(computeStats(editor));
      }, 300);
    };
    // Compare document identity, not only docChanged: resolving a review restores the saved
    // state with view.updateState (no transaction) and may then dispatch a step-less one.
    let lastDoc = editor.state.doc;
    const onTransaction = ({ transaction }) => {
      if (transaction.docChanged || transaction.getMeta('quillContentLoaded') || editor.state.doc !== lastDoc) {
        lastDoc = editor.state.doc;
        schedule();
      }
    };
    editor.on('transaction', onTransaction);
    setStats(computeStats(editor));
    return () => {
      clearTimeout(timer);
      editor.off('transaction', onTransaction);
    };
  }, [editor]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    setNow(Date.now());
  }, [saveState?.lastSavedAt, saveState?.status]);

  const readingMinutes = Math.max(1, Math.round(stats.words / 230));
  const save = describeSave(saveState, now);

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 2,
        px: 1.5,
        py: 0.75,
        mt: 1,
        fontSize: 12.5,
        color: 'text.secondary',
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'var(--q-surface, transparent)',
      }}
    >
      <Typography variant="caption" color="inherit">{stats.words.toLocaleString()} words</Typography>
      <Typography variant="caption" color="inherit" sx={{ display: { xs: 'none', sm: 'inline' } }}>
        {stats.characters.toLocaleString()} characters
      </Typography>
      <Typography variant="caption" color="inherit">{stats.words ? `${readingMinutes} min read` : ''}</Typography>
      {mode === 'template' && (
        <Typography variant="caption" color="inherit">{stats.fields} AI field{stats.fields === 1 ? '' : 's'}</Typography>
      )}
      {reviewCount > 0 && (
        <Typography variant="caption" sx={{ color: 'var(--q-accent-strong, #2F2096)', fontWeight: 600 }}>
          Reviewing {reviewCount} change{reviewCount === 1 ? '' : 's'}
        </Typography>
      )}
      <Box sx={{ flex: 1 }} />
      {save && (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: save.color }}>
          {save.icon}
          <Typography variant="caption" color="inherit">{save.label}</Typography>
        </Box>
      )}
      {onShowShortcuts && (
        <Tooltip title="Keyboard shortcuts (Ctrl+/)">
          <Box
            component="button"
            type="button"
            onClick={onShowShortcuts}
            aria-label="Keyboard shortcuts"
            sx={{ border: 0, bgcolor: 'transparent', color: 'inherit', cursor: 'pointer', display: 'inline-flex', p: 0.25, borderRadius: '6px', '&:hover': { bgcolor: 'action.hover' } }}
          >
            <Keyboard sx={{ fontSize: 16 }} />
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

export default memo(EditorStatusBar);
